import React, { useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Prompt, SkillFile } from '../types';
import { FilePlusIcon, UploadIcon, TrashIcon, ChevronRightIcon } from '../constants';
import { SKILL_DESCRIPTION_MAX, SKILL_SLUG_PATTERN, buildSkillMarkdown, slugify } from '../utils/itemKinds';

interface SkillHeaderProps {
  item: Prompt;
  onChange: (updates: Partial<Prompt>) => void;
}

/**
 * Kopf einer SKILL.md als bearbeitbares Formular. Die Optik folgt dem echten
 * Frontmatter-Block, damit klar ist, was später in der Datei steht.
 */
export const SkillHeader: React.FC<SkillHeaderProps> = ({ item, onChange }) => {
  const derivedSlug = slugify(item.name) || 'neuer-skill';
  const slug = item.slug ?? '';
  const slugInvalid = slug !== '' && !SKILL_SLUG_PATTERN.test(slug);
  const description = item.description ?? '';
  const descLength = description.length;
  const descTooLong = descLength > SKILL_DESCRIPTION_MAX;

  return (
    <section aria-labelledby="skill-header-title" className="rounded-lg border border-rule bg-raised overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-rule bg-kind-skill/[0.06]">
        <h3 id="skill-header-title" className="label-xs !text-kind-skill">Frontmatter · SKILL.md</h3>
        <span className="font-mono text-[11px] text-graphite">wird beim Export vorangestellt</span>
      </div>
      <div className="font-mono text-[13px] px-4 py-3 space-y-3">
        <div className="text-graphite select-none" aria-hidden="true">---</div>

        <div className="grid grid-cols-1 sm:grid-cols-[7.5rem_1fr] gap-x-3 gap-y-1 items-start">
          <label htmlFor="skill-slug" className="pt-2 text-kind-skill">name:</label>
          <div>
            <input
              id="skill-slug"
              type="text"
              value={slug}
              onChange={(e) => onChange({ slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              onBlur={() => { if (slug) onChange({ slug: slugify(slug) }); }}
              placeholder={derivedSlug}
              spellCheck={false}
              aria-invalid={slugInvalid}
              aria-describedby="skill-slug-hint"
              className={`input font-mono !text-[13px] ${slugInvalid ? '!border-red-600' : ''}`}
            />
            <p id="skill-slug-hint" className={slugInvalid ? 'field-error' : 'field-hint font-sans'}>
              {slugInvalid
                ? 'Nur Kleinbuchstaben, Ziffern und einzelne Bindestriche, z. B. code-audit.'
                : slug ? 'Ordnername und Aufrufname des Skills.' : `Leer gelassen, wird der Name aus dem Titel gebildet: ${derivedSlug}`}
            </p>
          </div>

          <label htmlFor="skill-description" className="pt-2 text-kind-skill">description:</label>
          <div>
            <textarea
              id="skill-description"
              value={description}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={4}
              placeholder="Wofür ist der Skill da und wann soll er greifen? Zum Beispiel: Prüft einen Blogartikel auf SEO und AdSense. Nutzen, wenn ein Artikel veröffentlicht werden soll."
              aria-invalid={descTooLong}
              aria-describedby="skill-description-hint"
              className="textarea font-sans !text-sm !min-h-[96px]"
            />
            <div id="skill-description-hint" className="flex items-start justify-between gap-3 mt-1.5">
              <p className="text-xs text-graphite font-sans">
                Der Assistent entscheidet allein anhand dieses Texts, ob er den Skill lädt. Auslöser und typische Formulierungen gehören hier hinein.
              </p>
              <span className={`shrink-0 text-[11px] tabular-nums ${descTooLong ? 'text-red-700 dark:text-red-400 font-semibold' : 'text-graphite'}`}>
                {descLength}/{SKILL_DESCRIPTION_MAX}
              </span>
            </div>
          </div>
        </div>

        <div className="text-graphite select-none" aria-hidden="true">---</div>
      </div>
    </section>
  );
};

interface SkillFilesProps {
  item: Prompt;
  onChange: (updates: Partial<Prompt>) => void;
  onToast: (message: string, type?: 'success' | 'error') => void;
  /** Überschrift und Leerhinweis, z. B. für Wissensdateien eines Gems */
  title?: string;
  emptyHint?: React.ReactNode;
}

const FILE_HINTS = ['references/checkliste.md', 'scripts/pruefe.py', 'assets/vorlage.html'];

/** Zusatzdateien neben der SKILL.md, z. B. Referenzen und Skripte */
export const SkillFiles: React.FC<SkillFilesProps> = ({ item, onChange, onToast, title = 'Zusatzdateien', emptyHint }) => {
  const files = item.files || [];
  const [openId, setOpenId] = useState<string | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const update = (next: SkillFile[]) => onChange({ files: next });
  const patchFile = (id: string, patch: Partial<SkillFile>) => update(files.map(f => (f.id === id ? { ...f, ...patch } : f)));

  const addEmpty = () => {
    const file: SkillFile = { id: uuidv4(), path: files.length === 0 ? 'references/' : '', content: '' };
    update([...files, file]);
    setOpenId(file.id);
  };

  const handleUpload = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const nextFiles = [...files];
    let addedCount = 0;
    let updatedCount = 0;
    for (const f of Array.from(list)) {
      if (f.size > 200_000) {
        onToast(`„${f.name}“ ist größer als 200 KB und wurde übersprungen.`, 'error');
        continue;
      }
      const dir = /\.(py|sh|js|ts|mjs)$/i.test(f.name) ? 'scripts' : 'references';
      const targetPath = `${dir}/${f.name}`;
      const content = await f.text();
      const existingIdx = nextFiles.findIndex(item => item.path === targetPath);
      if (existingIdx >= 0) {
        nextFiles[existingIdx] = { ...nextFiles[existingIdx], content };
        updatedCount++;
      } else {
        nextFiles.push({ id: uuidv4(), path: targetPath, content });
        addedCount++;
      }
    }
    if (addedCount > 0 || updatedCount > 0) {
      update(nextFiles);
      const messages: string[] = [];
      if (addedCount > 0) messages.push(`${addedCount} ${addedCount === 1 ? 'Datei hinzugefügt' : 'Dateien hinzugefügt'}`);
      if (updatedCount > 0) messages.push(`${updatedCount} ${updatedCount === 1 ? 'Datei aktualisiert' : 'Dateien aktualisiert'}`);
      onToast(messages.join(', '));
    }
    if (uploadRef.current) uploadRef.current.value = '';
  };

  return (
    <section aria-labelledby="skill-files-title">
      <div className="flex items-center justify-between mb-2">
        <h3 id="skill-files-title" className="label-xs">{title} <span className="font-mono normal-case tracking-normal">({files.length})</span></h3>
        <div className="flex gap-1">
          <button type="button" onClick={() => uploadRef.current?.click()} className="btn btn-ghost btn-sm !text-xs">
            <UploadIcon className="h-3.5 w-3.5" /> Hochladen
          </button>
          <button type="button" onClick={addEmpty} className="btn btn-secondary btn-sm !text-xs">
            <FilePlusIcon className="h-3.5 w-3.5" /> Datei anlegen
          </button>
          <input ref={uploadRef} type="file" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
        </div>
      </div>

      {files.length === 0 ? (
        <p className="text-sm text-graphite border border-dashed border-rule rounded-lg px-4 py-3">
          {emptyHint ?? <>Lange Nachschlagetexte, Checklisten oder Skripte gehören in eigene Dateien, z. B. <span className="font-mono text-ink/80">{FILE_HINTS.join(', ')}</span>. Die SKILL.md verweist dann darauf.</>}
        </p>
      ) : (
        <ul className="rounded-lg border border-rule divide-y divide-rule bg-raised overflow-hidden">
          {files.map(file => {
            const isOpen = openId === file.id;
            const lines = file.content ? file.content.split('\n').length : 0;
            return (
              <li key={file.id}>
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : file.id)}
                    className="icon-btn !h-7 !w-7"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? `${file.path || 'Datei'} einklappen` : `${file.path || 'Datei'} bearbeiten`}
                  >
                    <ChevronRightIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </button>
                  <input
                    type="text"
                    value={file.path}
                    onChange={(e) => patchFile(file.id, { path: e.target.value })}
                    placeholder="references/datei.md"
                    spellCheck={false}
                    aria-label="Dateipfad"
                    className="flex-1 min-w-0 bg-transparent border-0 shadow-none focus:ring-0 font-mono text-[13px] text-ink px-1 py-1 rounded focus:bg-paper"
                  />
                  <span className="font-mono text-[11px] text-graphite shrink-0 tabular-nums">{lines} Z.</span>
                  <button
                    type="button"
                    onClick={() => update(files.filter(f => f.id !== file.id))}
                    className="icon-btn !h-7 !w-7 hover:!text-red-700 dark:hover:!text-red-400"
                    aria-label={`${file.path || 'Datei'} entfernen`}
                    title="Datei entfernen"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                {isOpen && (
                  <div className="px-3 pb-3">
                    <textarea
                      value={file.content}
                      onChange={(e) => patchFile(file.id, { content: e.target.value })}
                      rows={10}
                      spellCheck={false}
                      aria-label={`Inhalt von ${file.path || 'Datei'}`}
                      className="textarea font-mono !text-[13px]"
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

/** Fertige SKILL.md, wie sie exportiert und kopiert wird */
export const SkillPreview: React.FC<{ item: Prompt }> = ({ item }) => {
  const markdown = useMemo(() => buildSkillMarkdown(item), [item]);
  return (
    <details className="group rounded-lg border border-rule bg-raised">
      <summary className="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        <ChevronRightIcon className="h-4 w-4 text-graphite transition-transform group-open:rotate-90" />
        <span className="label-xs">Fertige SKILL.md ansehen</span>
        <span className="ml-auto font-mono text-[11px] text-graphite">{markdown.split('\n').length} Zeilen</span>
      </summary>
      <pre className="px-4 pb-4 pt-1 text-[12.5px] leading-relaxed font-mono text-ink whitespace-pre-wrap break-words max-h-[420px] overflow-auto scrollbar-thin">{markdown}</pre>
    </details>
  );
};
